import { CARD_CLASS, LABEL_CLASS, SegmentedBar } from '@shared/ui';
import { cn } from '@shared/utils';
import type { DailySummary, MealSlot } from '@diet/types';
import { MacroRow } from './macro-row';
import { formatCalories, MEAL_SLOTS, MEAL_SLOT_LABELS } from '@diet/utils';

export interface DailySummaryCardProps {
  summary: DailySummary;
  /** 檢視的是否為今天，影響標題用語 */
  isToday: boolean;
  /** 每日目標熱量（kcal）；null 為未設定，此時不顯示達成度 */
  goal: number | null;
  className?: string;
}

/*
 * 分佈條的餐別配色。色值與選色理由見 `apps/diet/src/styles/meal-colors.css`。
 *
 * 這裡用任意值語法而非具名 utility：餐別色是 diet 的產品專屬色，不放進
 * `@shared/ui-system` 的 `@theme`，因此沒有對應的 `bg-meal-*` utility 可用。
 */
const SLOT_BAR_CLASS: Record<MealSlot, string> = {
  breakfast: 'bg-[var(--color-meal-breakfast)]',
  lunch: 'bg-[var(--color-meal-lunch)]',
  dinner: 'bg-[var(--color-meal-dinner)]',
  snack: 'bg-[var(--color-meal-snack)]',
};

/**
 * 當日總覽：大字總熱量、與目標的差距、餐別分佈條。
 * 無紀錄時仍完整呈現（總計 0），維持版面骨架不隨狀態跳動。
 *
 * 設有每日目標時，分佈條同時兼任達成度條。不另加第二條進度條：兩條並排的橫條會
 * 互搶注意力，而它們講的其實是同一天的同一個量。
 */
export function DailySummaryCard({
  summary,
  isToday,
  goal,
  className,
}: DailySummaryCardProps) {
  const { total, bySlot, count, macros, missingMacroCount } = summary;
  /* 全部紀錄都沒有營養素時整段不渲染 —— 一排 0g 只是雜訊，不是資訊 */
  const hasAnyMacro = count > missingMacroCount;
  const slotsWithValue = MEAL_SLOTS.filter((slot) => bySlot[slot] > 0);

  const isOverGoal = goal !== null && total > goal;
  const remaining = goal !== null ? goal - total : 0;

  return (
    <section
      className={cn(CARD_CLASS, 'flex flex-col gap-4 p-5', className)}
      aria-label="當日總覽"
    >
      <div className="flex flex-col gap-1">
        <span className={LABEL_CLASS}>{isToday ? '今日攝取' : '當日攝取'}</span>

        <p className="font-display text-4xl leading-none tracking-tight tabular-nums">
          {formatCalories(total)}
          {/* 目標接在總量後面而非另起一行：兩個數字要並排才讀得出「還差多少」 */}
          <span className="ml-1.5 text-sm tracking-normal text-muted-foreground">
            {goal !== null && `/ ${formatCalories(goal)} `}kcal
          </span>
        </p>

        {/*
          只講「還差多少」與「還沒開始」，其餘細節留給下方的餐別清單。
          兩者皆無時整行不渲染，避免空的 <p> 多出 mt-1 的間距。
        */}
        {(goal !== null || count === 0) && (
          <p className="mt-1 text-xs text-muted-foreground">
            {goal !== null && (
              <span className={cn(isOverGoal && 'text-error')}>
                {isOverGoal
                  ? `超出 ${formatCalories(-remaining)} kcal`
                  : `剩餘 ${formatCalories(remaining)} kcal`}
              </span>
            )}
            {/* 分隔點只在兩段都在時才需要 */}
            {goal !== null && count === 0 && <span className="mx-1.5">·</span>}
            {count === 0 && '尚未有任何紀錄'}
          </p>
        )}
      </div>

      {/*
        設有每日目標時，以目標為滿格：總量相對於目標成長，超標後在目標的位置畫刻度線。
        餐別仍各自分段，不必再另加一條達成度條。
      */}
      <SegmentedBar
        segments={slotsWithValue.map((slot) => ({
          key: slot,
          label: MEAL_SLOT_LABELS[slot],
          value: bySlot[slot],
          colorClass: SLOT_BAR_CLASS[slot],
        }))}
        formatValue={formatCalories}
        limit={goal ?? undefined}
        ariaLabel={[
          goal !== null &&
            `目標 ${formatCalories(goal)} kcal，${
              isOverGoal
                ? `超出 ${formatCalories(-remaining)}`
                : `剩餘 ${formatCalories(remaining)}`
            } kcal`,
          ...slotsWithValue.map(
            (slot) =>
              `${MEAL_SLOT_LABELS[slot]} ${formatCalories(bySlot[slot])} kcal`,
          ),
        ]
          .filter(Boolean)
          .join('、')}
        size="sm"
        legend="inline"
      />

      {hasAnyMacro && (
        <MacroRow macros={macros} missingCount={missingMacroCount} />
      )}
    </section>
  );
}
